import { useState, useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { Images, Eye, X } from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/lib/utils";
import { useI18n } from "@/i18n/I18nContext";
import { conversationWrite, CHAT_TEXT } from "@/lib/conversationWrite";
import { convTitle, convContext, isDirect, chatHref, normalizeMessages, normalizeMedia, reportTarget, reportPayload, REPORT_REASONS, attachmentErrors, ALT_MAX } from "@/lib/v3Messages";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useCore } from "../useCore";
import { mutate } from "../actions";
import { ConnectionError } from "../components/ConnectionError";
import { Loading, Unavailable, ErrorNote } from "../components/kit";
import { V3Title, V3Columns, V3Actions, StatusBanner, DetailRow, DetailList, AsideCard, SettingRow, V3Switch, ReadingPreview, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { V3TextArea, V3_GHOST } from "../components/v3app";
import { InfoBox } from "../components/v3people";
import { WithChat, ChatAside, ChatMissing, messagesPath } from "../components/v3messages";
import { useLocalImage } from "../components/ThreadTools";

const Blocked = ({ id, children }) => <div className="mt-[12px]" id={id}><Unavailable testid={id}>{children}</Unavailable></div>;
const BackLink = ({ c, testid, label = "Zurück" }) => <Link to={chatHref(c)} data-testid={testid} className={V3_SECONDARY}>{label}</Link>;
const SLOT = "mb-[21px] aspect-[3/2] w-full rounded-[12px] lg:aspect-[742/335]";

function MediaSlot({ text: body, children, testid }) {
  return (
    <div data-testid={testid} className={`${SLOT} flex flex-col items-center justify-center gap-[10px] border border-dashed border-v3-line bg-v3-lilac text-center text-v3-muted`}>
      <Images className="h-[34px] w-[34px] text-v3-magenta" strokeWidth={1.6} aria-hidden="true" />
      <span className="px-4 text-[14px] font-bold">{body}</span>
      {children}
    </div>
  );
}

// M78: GET /member/conversations/{id}/media (proposed, D-60); a missing endpoint shows "Noch nicht verfügbar", nothing else is loaded.
function Media({ c }) {
  const res = useCore(`/member/conversations/${encodeURIComponent(c.id)}/media`, (d) => ({ list: normalizeMedia(d) }));
  const [i, setI] = useState(0);
  const [open, setOpen] = useState(false);
  const missing = res.error === "notfound";
  if (res.error && !missing) return <ConnectionError onRetry={res.reload} />;
  if (!res.data && !missing) return <Loading />;
  const list = missing ? null : res.data.list;
  const item = list && list.length ? list[i % list.length] : null;
  const none = missing ? "Noch nicht verfügbar" : list === null ? "Nicht lesbar" : "Noch keine Medien geteilt";
  const who = `${convTitle(c)} · ${convContext(c)}`;
  return (
    <div data-m="M78" data-testid="chat-media">
      <V3Title title="Geteilte Medien im Gespräch" intro="Geteilte Medien bleiben auf dieses berechtigte Gespräch begrenzt." testid="chat-media-title" />
      <V3Columns layout="detail" aside={<ChatAside text={who} />} main={<>
        {item ? <img src={item.url} alt={item.alt} className={`${SLOT} object-cover`} data-testid="chat-media-image" />
          : <MediaSlot text={missing ? "Medien sind noch nicht verfügbar" : none} testid="chat-media-slot" />}
        <DetailList>
          <DetailRow label="Gespräch" value={who} testid="chat-media-conv" />
          <DetailRow label="Bild" value={item ? item.title : none} testid="chat-media-name" />
          <DetailRow label="Beschreibung" value={item ? item.alt : none} testid="chat-media-alt" />
          {list && list.length > 1
            ? <DetailRow label="Weitere Medien" value={`${(i % list.length) + 1} von ${list.length} · Nächstes Medium zeigen`} onClick={() => setI((x) => x + 1)} testid="chat-media-more" />
            : <DetailRow label="Weitere Medien" value="Nur in diesem Gespräch öffnen" testid="chat-media-more" />}
        </DetailList>
        {missing && <Blocked id="chat-media-unavailable">Geteilte Medien liefert der gemeinsame Core noch nicht. Es werden keine Bilder abgerufen, gezeigt oder gespeichert.</Blocked>}
        <V3Actions>
          <button type="button" onClick={() => setOpen(true)} disabled={!item} aria-describedby={missing ? "chat-media-unavailable" : undefined} data-testid="chat-media-open" className={V3_PRIMARY}>Medium öffnen</button>
          <BackLink c={c} testid="chat-media-back" />
        </V3Actions>
        {item && <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent data-testid="chat-media-dialog" className="max-h-[92vh] overflow-y-auto sm:max-w-3xl">
            <DialogHeader><DialogTitle className="text-v3-ink">{item.title}</DialogTitle><DialogDescription className="text-v3-muted">{item.alt}</DialogDescription></DialogHeader>
            <img src={item.url} alt={item.alt} className="w-full rounded-[10px] object-contain" />
          </DialogContent>
        </Dialog>}
      </>} />
    </div>
  );
}

// The only file input sits inside its visible label, so keyboard focus is shown on the label.
function FileLabel({ onPick, className, error, children }) {
  return (
    <label className={`${className} focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-v3-magenta`} data-testid="chat-attach-pick">
      {children}
      <input id="chat-attach-file" type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={onPick} className="sr-only" data-testid="chat-attach-file" aria-describedby={error ? "chat-attach-file-error" : undefined} />
    </label>
  );
}

// M79: local preview and check only; sending needs POST /member/media + attachments on messages (D-10, D-61). Nothing is uploaded.
function Attach({ c }) {
  const img = useLocalImage();
  const [alt, setAlt] = useState("");
  const [errors, setErrors] = useState({});
  const [review, setReview] = useState(false);
  const w = conversationWrite(c);
  const pick = (e) => { img.pick(e); setReview(false); setErrors({}); };
  const remove = () => { img.clear(); setReview(false); };
  const check = () => { const e = attachmentErrors({ file: img.file, alt }); setErrors(e); setReview(!Object.keys(e).length); };
  return (
    <div data-m="M79" data-testid="chat-attach">
      <V3Title title="Anhang vor dem Senden prüfen" intro="Prüfe Bild und Beschreibung, bevor du den Anhang sendest." testid="chat-attach-title" />
      <V3Columns layout="detail" aside={<ChatAside text={convTitle(c)} />} main={!w.canWrite ? (<>
        <div role="status" data-testid="chat-attach-readonly"><StatusBanner tone="notice" title="Nur lesen" text={w.hint} /></div>
        <V3Actions><BackLink c={c} testid="chat-attach-back" label="Zum Gespräch" /></V3Actions>
      </>) : (<>
        {img.url ? <img src={img.url} alt={alt.trim() || "Vorschau ohne Bildbeschreibung"} className={`${SLOT} object-cover`} data-testid="chat-attach-preview" />
          : <MediaSlot text="Noch kein Bild ausgewählt" testid="chat-attach-slot">
            <FileLabel onPick={pick} className="mt-[4px] inline-flex min-h-[44px] cursor-pointer items-center gap-2 rounded-[8px] border border-v3-line bg-white px-[14px] text-[14px] font-bold text-v3-ink hover:bg-v3-lilac" error={errors.file}>Bild auswählen</FileLabel>
          </MediaSlot>}
        {errors.file && <p id="chat-attach-file-error" role="alert" data-testid="chat-attach-file-error" className="mb-[12px] text-[13px] font-bold text-red-800">{errors.file}</p>}
        <DetailList>
          <DetailRow label="Empfänger" value={convTitle(c)} testid="chat-attach-recipient" />
          <DetailRow label="Bild" value={img.file ? img.file.name : "Noch kein Bild ausgewählt"} testid="chat-attach-name" />
        </DetailList>
        {img.file && <div className="mt-[6px] flex flex-wrap gap-2">
          <FileLabel onPick={pick} className={`${V3_GHOST} cursor-pointer`} error={errors.file}>Anderes Bild wählen</FileLabel>
          <button type="button" onClick={remove} data-testid="chat-attach-remove" className={V3_GHOST}><X className="h-4 w-4" aria-hidden="true" />Bild entfernen</button>
        </div>}
        <div className="mt-[11px]">
          <V3TextArea id="chat-attach-alt" label="Bildbeschreibung" max={ALT_MAX} value={alt} onChange={(e) => { setAlt(e.target.value); setReview(false); }} rows={3} error={errors.alt}
            placeholder="Was ist auf dem Bild zu sehen?" hint="Beschreibe sachlich, was zu sehen ist – ohne Vermutungen über Personen oder Diagnosen." />
        </div>
        <DetailRow label="Status" value={review ? "Geprüft · Anhang noch nicht gesendet" : "Anhang noch nicht gesendet"} testid="chat-attach-status" />
        {review && <Blocked id="chat-attach-blocked">Fotos in Nachrichten sind im gemeinsamen Core noch nicht eingerichtet. Das Bild wurde nicht hochgeladen und wird nicht gespeichert.</Blocked>}
        <V3Actions>
          {review
            ? <button type="button" disabled aria-describedby="chat-attach-blocked" data-testid="chat-attach-send" className={V3_PRIMARY}>Anhang senden</button>
            : <button type="button" onClick={check} data-testid="chat-attach-check" className={V3_PRIMARY}>Anhang prüfen</button>}
          <BackLink c={c} testid="chat-attach-back" />
        </V3Actions>
      </>)} />
    </div>
  );
}

function ReasonSelect({ value, onChange, error }) {
  const { t } = useI18n();
  return (
    <div className={`min-h-[73px] rounded-[10px] border bg-white px-[16px] pb-[10px] pt-[10px] focus-within:border-v3-magenta ${error ? "border-red-700" : "border-v3-line"}`}>
      <label htmlFor="chat-report-reason" className="block text-[13px] font-bold leading-[18px] text-v3-muted lg:text-[14px] lg:leading-[19px]">Meldegrund</label>
      <select id="chat-report-reason" value={value} onChange={(e) => onChange(e.target.value)} data-testid="chat-report-reason" aria-invalid={error ? "true" : undefined} aria-describedby={error ? "chat-report-reason-error" : undefined}
        className="mt-[2px] min-h-[30px] w-full cursor-pointer appearance-none bg-transparent text-[14.5px] text-v3-ink outline-none lg:text-[16px]">
        <option value="">Bitte sachlich auswählen</option>
        {REPORT_REASONS.map((r) => <option key={r} value={r}>{t(`member.p2b.reason_${r}`)}</option>)}
      </select>
      {error && <span id="chat-report-reason-error" role="alert" data-testid="chat-report-reason-error" className="mt-[4px] block text-[13px] font-bold text-red-800">{error}</span>}
    </div>
  );
}

// M80: POST /member/reports {reported_member_id, reason, conversation_id, message_id} (Core 1.1); success only after the Core answers.
function ReportForm({ c, m }) {
  const { t } = useI18n();
  const [reason, setReason] = useState("");
  const [stage, setStage] = useState("edit");
  const [preview, setPreview] = useState(false);
  const [error, setError] = useState("");
  const [sendError, setSendError] = useState("");
  const [unclear, setUnclear] = useState(false);
  const [busy, setBusy] = useState(false);
  const flight = useRef(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const target = reportTarget(c, m);
  const payload = reportPayload(c, m, reason);
  const context = isDirect(c) ? "Bestätigter Kontakt" : `Kreisgespräch · ${convTitle(c)}`;
  const choose = (v) => { setReason(v); setError(""); setStage("edit"); setUnclear(false); setSendError(""); };
  const check = () => { if (!reason) { setError("Bitte wähle einen Meldegrund."); return; } setStage("review"); };
  const submit = async () => {
    if (!payload || flight.current) return;
    flight.current = true; setBusy(true); setSendError(""); setUnclear(false);
    const r = await mutate("post", "/member/reports", payload, "Die Meldung wurde nicht übermittelt");
    flight.current = false;
    if (!alive.current) return;
    setBusy(false);
    if (!r.rejected && typeof r.status === "number" && r.status >= 200 && r.status < 300) { setStage("done"); toast.success("Meldung übermittelt."); return; }
    if (r.status === 404) { setSendError(CHAT_TEXT.report_target_missing); return; }
    if (r.rejected) { setSendError(r.rejected); return; }
    setUnclear(true);
  };
  if (stage === "done") return (
    <div data-m="M80" data-testid="chat-report-done">
      <V3Title title="Nachricht melden" intro="Es wird nur die von dir gewählte Nachricht mit notwendigem Kontext gemeldet." testid="chat-report-title" />
      <V3Columns layout="detail" aside={<ChatAside text={context} />} main={<>
        <div role="status"><StatusBanner title="Meldung übermittelt" text="Das VISIBILIS-Team prüft die Meldung. Die Person wird dadurch nicht automatisch blockiert." testid="chat-report-success" /></div>
        <V3Actions>
          <Link to={chatHref(c)} data-testid="chat-report-to-chat" className={V3_PRIMARY}>Zum Gespräch</Link>
          {target && <Link to={`/kontakte/blockieren/${encodeURIComponent(target)}`} data-testid="chat-report-block-after" className={V3_SECONDARY}>Blockieren</Link>}
        </V3Actions>
      </>} />
    </div>
  );
  return (
    <div data-m="M80" data-testid="chat-report">
      <V3Title title="Nachricht melden" intro="Es wird nur die von dir gewählte Nachricht mit notwendigem Kontext gemeldet." testid="chat-report-title" />
      <V3Columns layout="detail" aside={<ChatAside text={context} />} main={<>
        <DetailList>
          <DetailRow label="Gespräch" value={context} testid="chat-report-conv" />
          <DetailRow label="Ausgewählter Ausschnitt" value="Eine Nachricht · Vorschau prüfen" onClick={() => setPreview((p) => !p)} expanded={preview} testid="chat-report-excerpt" />
          {preview && <blockquote className="whitespace-pre-wrap break-words rounded-[10px] bg-v3-lilac px-[16px] py-[12px] text-[14.5px] leading-[21px] text-v3-ink lg:text-[15px]" data-testid="chat-report-preview">{m.body}</blockquote>}
          <ReasonSelect value={reason} onChange={choose} error={error} />
          <DetailRow label="Blockieren" value="Bei Bedarf getrennt entscheiden" to={target ? `/kontakte/blockieren/${encodeURIComponent(target)}` : undefined} testid="chat-report-block" />
        </DetailList>
        {!target && <Blocked id="chat-report-no-sender">Für diese Nachricht fehlt die Absenderangabe im gemeinsamen Core. Sie kann hier noch nicht gemeldet werden.</Blocked>}
        {stage === "review" && <div className="mt-[11px]"><InfoBox title="Meldung prüfen" text={`Grund: ${t(`member.p2b.reason_${reason}`)}. Gemeldet wird nur diese eine Nachricht. Die Person wird nicht automatisch blockiert.`} testid="chat-report-review" /></div>}
        {unclear && <div role="status" data-testid="chat-report-unclear"><StatusBanner tone="notice" title="Status unklar" text="Die Verbindung wurde unterbrochen. Ob die Meldung angekommen ist, ist unklar. Du kannst sie bewusst erneut senden." /></div>}
        {sendError && <div className="mt-[12px]"><ErrorNote testid="chat-report-error">{sendError}</ErrorNote></div>}
        <V3Actions>
          {stage === "review"
            ? <button type="button" onClick={submit} disabled={busy || !payload} data-testid="chat-report-submit" className={V3_PRIMARY}>{busy ? "Wird übermittelt …" : unclear ? "Bewusst erneut senden" : "Meldung senden"}</button>
            : <button type="button" onClick={check} disabled={!target} aria-describedby={target ? undefined : "chat-report-no-sender"} data-testid="chat-report-check" className={V3_PRIMARY}>Meldung prüfen</button>}
          {stage === "review"
            ? <button type="button" onClick={() => setStage("edit")} disabled={busy} data-testid="chat-report-edit" className={V3_SECONDARY}>Ändern</button>
            : <BackLink c={c} testid="chat-report-back" />}
        </V3Actions>
      </>} />
    </div>
  );
}

function ReportLoader({ c }) {
  const { messageId } = useParams();
  const thread = useCore(messagesPath(c), (d) => ({ list: normalizeMessages(d) }));
  if (thread.error === "notfound" || thread.error === "forbidden") return <ChatMissing />;
  if (thread.error) return <ConnectionError onRetry={thread.reload} />;
  if (!thread.data) return <Loading />;
  const m = (thread.data.list || []).find((x) => x.id === String(messageId));
  if (!m || m.mine) return (
    <div data-testid="chat-report-missing">
      <V3Title title="Nachricht melden" intro="Diese Nachricht kann hier nicht gemeldet werden." testid="chat-report-title" />
      <V3Columns layout="detail" aside={<ChatAside text={convTitle(c)} />} main={<>
        <StatusBanner tone="sand" title="Nachricht nicht verfügbar" text={m ? "Eigene Nachrichten werden nicht gemeldet." : "Die Nachricht ist nicht (mehr) im Verlauf."} testid="chat-report-missing-banner" />
        <V3Actions><BackLink c={c} testid="chat-report-back" label="Zum Gespräch" /></V3Actions>
      </>} />
    </div>
  );
  return <ReportForm key={m.id} c={c} m={m} />;
}

// M81: muting needs PUT /member/conversations/{id}/mute (D-62); the switches stay locked, nothing is saved.
function Mute({ c }) {
  const title = convTitle(c);
  const aside = (<>
    <AsideCard icon={Eye} title="Deine Lesevorschau" text="Eine gut lesbare Ansicht unterstützt deinen eigenen nächsten Schritt." testid="chat-mute-reading" />
    <ReadingPreview action={<Link to="/einstellungen/darstellung" data-testid="chat-mute-next" className={cn(V3_PRIMARY, "lg:w-full")}>Nächsten Schritt ansehen<span className="sr-only">: Darstellung einstellen</span></Link>} />
  </>);
  return (
    <div data-m="M81" data-testid="chat-mute">
      <V3Title title="Gruppenchat stummschalten" intro="Diese Auswahl betrifft nur Benachrichtigungen aus diesem Gruppenchat." testid="chat-mute-title" />
      <V3Columns layout="settings" aside={aside} main={c.type !== "group" ? (<>
        <InfoBox title="Nur für Kreisgespräche" text="Persönliche Gespräche und Kanäle werden hier nicht stummgeschaltet." testid="chat-mute-not-group" />
        <V3Actions><BackLink c={c} testid="chat-mute-back" label="Zum Gespräch" /></V3Actions>
      </>) : (<>
        <div className="space-y-[11px]">
          <SettingRow title="Gruppenchat" value={title} testid="chat-mute-chat">
            <V3Switch checked={false} disabled onChange={() => {}} label={`Gruppenchat ${title} stummschalten (noch nicht verfügbar)`} describedBy="chat-mute-unavailable" testid="chat-mute-switch" />
          </SettingRow>
          <SettingRow title="Dauer" value="24 Stunden" testid="chat-mute-duration">
            <V3Switch checked={false} disabled onChange={() => {}} label="Für 24 Stunden stummschalten (noch nicht verfügbar)" describedBy="chat-mute-unavailable" testid="chat-mute-duration-switch" />
          </SettingRow>
          <SettingRow title="Private Nachrichten" value="Bleiben unverändert" testid="chat-mute-private" />
          <SettingRow title="Stummschaltung" value="Jederzeit wieder aufheben" testid="chat-mute-undo" />
        </div>
        <Blocked id="chat-mute-unavailable">Stummschalten ist im gemeinsamen Core noch nicht eingerichtet. Es wird nichts gespeichert; Benachrichtigungen aus diesem Gruppenchat bleiben unverändert.</Blocked>
        <V3Actions>
          <button type="button" disabled aria-describedby="chat-mute-unavailable" data-testid="chat-mute-apply" className={V3_PRIMARY}>Auswahl übernehmen</button>
          <BackLink c={c} testid="chat-mute-back" />
        </V3Actions>
      </>)} />
    </div>
  );
}

export const MessageMedia = () => <WithChat>{(c) => <Media c={c} />}</WithChat>;
export const MessageAttach = () => <WithChat>{(c) => <Attach c={c} />}</WithChat>;
export const MessageReport = () => <WithChat>{(c) => <ReportLoader c={c} />}</WithChat>;
export const MessageMute = () => <WithChat>{(c) => <Mute c={c} />}</WithChat>;
